/**
 * Durable Object Event Models — vpage-fe (Angular 21)
 *
 * Port từ `vpage-fe1/src/app/models/do-event.model.ts` để giữ contract trùng khớp giữa
 * FE cũ (đang chạy production) và FE mới. Không đổi tên enum / shape payload.
 *
 * ## Kiến trúc Event
 * Chỉ có 4 channel event chính tương ứng với 4 loại room:
 * - `page:event` → mọi sự kiện liên quan đến page
 * - `business:event` → mọi sự kiện liên quan đến doanh nghiệp
 * - `user:event` → mọi sự kiện gửi riêng tới user
 * - `mobile:transfer` → dữ liệu từ mobile app
 *
 * Mỗi message gửi kèm field `type` (PAGE_EVENT_TYPE / BUSINESS_EVENT_TYPE / …)
 * để phân biệt sự kiện cụ thể bên trong channel.
 *
 * @example
 * doService.onPageEvent(PAGE_EVENT_TYPE.CONVERSATION_TYPING).subscribe(...)
 * doService.emitTyping(pageId, { conversationId, userName, userId })
 *
 * @see [ADR-0002](../../../../../../../../agent-vpage/tracking/decision-log.md)
 */

// ─── Room types ───────────────────────────────────────────────────────────────

export enum DO_ROOM_TYPE {
  USER = 'user',
  PAGE = 'page',
  BUSINESS = 'business',
}

// ─── Main channel events (chỉ 4 + lifecycle nội bộ) ──────────────────────────

export enum DO_EVENT {
  PAGE_EVENT = 'page:event',
  BUSINESS_EVENT = 'business:event',
  USER_EVENT = 'user:event',
  MOBILE_TRANSFER = 'mobile:transfer',

  // Lifecycle nội bộ FE — không gửi qua dây
  __CONNECTED = '__connected',
  __DISCONNECTED = '__disconnected',
  __ERROR = '__error',
}

// ─── Sub-event types ──────────────────────────────────────────────────────────

export enum PAGE_EVENT_TYPE {
  CONVERSATION_TYPING = 'conversation:typing',
  CONVERSATION_STOP_TYPING = 'conversation:stop-typing',
  CONVERSATION_CHANGES = 'conversation:changes',
  CONVERSATION_RECOMMEND = 'conversation:recommend-product',
  /** Business Calling (WebRTC) — FE13 `_handleBusinessCallingData()` */
  CONVERSATION_CALLING = 'conversation:calling',

  TAG_CHANGED = 'tag:changed',
  QUICK_MESSAGE_CHANGED = 'quick-message:changed',
  PAGE_RECONNECTED = 'page:reconnected',
  PAGE_TOKEN_EXPIRED = 'page:token-expired',
  PAGE_TRACKING = 'page:tracking',
}

export enum BUSINESS_EVENT_TYPE {
  SETTINGS_CHANGED = 'settings:changed',
  MEMBER_CHANGED = 'member:changed',
  TAG_CHANGED = 'tag:changed',
  QUICK_MESSAGE_CHANGED = 'quick-message:changed',
}

export enum USER_EVENT_TYPE {
  REMINDER = 'reminder',
  NOTIFICATION = 'notification',
  /** Gửi tin nhắn xem thử tới Messenger thành công. */
  USER_PAGE_MESSAGE_PREVIEW_SUCCESS = 'page_message_preview_success',
}

export enum MOBILE_TRANSFER_TYPE {
  PHONE_NUMBER = 'phone-number',
  DATA = 'data',
}

// ─── Generic wrappers ─────────────────────────────────────────────────────────

/** Cấu trúc message gửi/nhận qua DO WebSocket. `data.type` xác định sub-event. */
export interface DoMessage<T = unknown> {
  event: DO_EVENT | string;
  data: T;
}

/** Message đã enrich metadata room sau khi nhận từ WebSocket. */
export interface DoIncomingEvent<T = unknown> extends DoMessage<T> {
  roomId: string;
  roomType: DO_ROOM_TYPE;
}

/** Base cho mọi sub-event payload — luôn có field `type`. */
export interface DoSubEventPayload<TType extends string = string> {
  type: TType;
}

// ─── Payload types ────────────────────────────────────────────────────────────

export interface TypingPayload extends DoSubEventPayload<PAGE_EVENT_TYPE.CONVERSATION_TYPING> {
  conversationId?: string;
  userName?: string;
  userId?: string;
}

export interface StopTypingPayload extends DoSubEventPayload<PAGE_EVENT_TYPE.CONVERSATION_STOP_TYPING> {
  conversationId?: string;
  userName?: string;
  userId?: string;
}

export interface ConversationChangesPayload
  extends DoSubEventPayload<PAGE_EVENT_TYPE.CONVERSATION_CHANGES> {
  conversation?: unknown;
  messages?: unknown[];
  reminders?: unknown[];

  [key: string]: unknown;
}

export interface RecommendProductPayload
  extends DoSubEventPayload<PAGE_EVENT_TYPE.CONVERSATION_RECOMMEND> {
  conversationId?: string;
  products?: unknown[];

  [key: string]: unknown;
}

export interface TagChangedPayload
  extends DoSubEventPayload<PAGE_EVENT_TYPE.TAG_CHANGED | BUSINESS_EVENT_TYPE.TAG_CHANGED> {
  [key: string]: unknown;
}

export interface QuickMessageChangedPayload
  extends DoSubEventPayload<
    PAGE_EVENT_TYPE.QUICK_MESSAGE_CHANGED | BUSINESS_EVENT_TYPE.QUICK_MESSAGE_CHANGED
  > {
  [key: string]: unknown;
}

export interface ReminderPayload extends DoSubEventPayload<USER_EVENT_TYPE.REMINDER> {
  items?: unknown[];

  [key: string]: unknown;
}

export interface PageMessagePreviewSuccessPayload
  extends DoSubEventPayload<USER_EVENT_TYPE.USER_PAGE_MESSAGE_PREVIEW_SUCCESS> {
  payload?: {
    businessId?: number;
    pageId?: string | number;
    psid?: string | null;
    createdById?: string;
    type?: number;
    fbName?: string | null;
    createdAt?: number;

    [key: string]: unknown;
  };

  [key: string]: unknown;
}

export interface MobileTransferPayload extends DoSubEventPayload<MOBILE_TRANSFER_TYPE> {
  [key: string]: unknown;
}
